// =============================================================================
// ACTIONS SERVICE - Firebase CRUD for Actions
// =============================================================================

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
  FieldValue,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Action } from '../types';

const COLLECTION_NAME = 'actions';

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

/**
 * Create a new action in Firestore
 * @param ownerId - Firebase Auth UID
 * @param goalId - Parent goal ID
 * @param milestoneId - Parent milestone ID (can be null for orphan actions)
 * @param data - Action data (without id, createdAt, ownerId, goalId, milestoneId)
 * @returns The created action's ID
 */
export async function createAction(
  ownerId: string,
  goalId: string,
  milestoneId: string | null,
  data: Omit<Action, 'id' | 'createdAt' | 'ownerId' | 'goalId' | 'milestoneId'>
): Promise<string> {
  const actionData: Omit<Action, 'id'> = {
    ...data,
    ownerId,
    goalId,
    milestoneId,
    createdAt: Date.now(),
  };

  const docRef = await addDoc(collection(db, COLLECTION_NAME), actionData);
  
  // Update the document with its own ID for consistency
  await updateDoc(docRef, { id: docRef.id });
  
  return docRef.id;
}

// -----------------------------------------------------------------------------
// READ
// -----------------------------------------------------------------------------

/**
 * Get all actions for a specific goal
 * @param ownerId - Firebase Auth UID
 * @param goalId - Parent goal ID
 * @returns Array of Action objects
 */
export async function getActionsByGoal(
  ownerId: string,
  goalId: string
): Promise<Action[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('goalId', '==', goalId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Action))
    .sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Get all actions for a specific milestone
 * @param ownerId - Firebase Auth UID
 * @param milestoneId - Parent milestone ID
 * @returns Array of Action objects
 */
export async function getActionsByMilestone(
  ownerId: string,
  milestoneId: string
): Promise<Action[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId),
    where('milestoneId', '==', milestoneId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Action))
    .sort((a, b) => a.createdAt - b.createdAt);
}

/**
 * Get all actions for a user (across all goals)
 * @param ownerId - Firebase Auth UID
 * @returns Array of Action objects
 */
export async function getAllActions(ownerId: string): Promise<Action[]> {
  // Simple query without orderBy to avoid needing composite index
  const q = query(
    collection(db, COLLECTION_NAME),
    where('ownerId', '==', ownerId)
  );

  const snapshot = await getDocs(q);
  
  // Sort in memory
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data(),
    } as Action))
    .sort((a, b) => a.createdAt - b.createdAt);
}

// -----------------------------------------------------------------------------
// UPDATE
// -----------------------------------------------------------------------------

/**
 * Update an existing action
 * @param actionId - The action's document ID
 * @param data - Partial action data to update (can include FieldValue for deletions)
 */
export async function updateAction(
  actionId: string,
  data: Record<string, any> // Accept any field including FieldValue for deletions
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, actionId);
  await updateDoc(docRef, data);
}

/**
 * Toggle the completed status of an action
 * @param actionId - The action's document ID
 * @param completed - The new completed status
 */
export async function toggleActionComplete(
  actionId: string,
  completed: boolean
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, actionId);
  await updateDoc(docRef, { completed });
}

// -----------------------------------------------------------------------------
// DELETE
// -----------------------------------------------------------------------------

/**
 * Delete an action
 * @param actionId - The action's document ID
 */
export async function deleteAction(actionId: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, actionId);
  await deleteDoc(docRef);
}
